import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useClinicianPortal } from "@/hooks/useClinicianPortal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Users, FileText, Settings, BarChart3, CreditCard, MessageSquare, Stethoscope } from "lucide-react";
import { ClinicianDashboard } from "@/components/clinician/ClinicianDashboard";
import { ClinicianCalendar } from "@/components/clinician/ClinicianCalendar";
import { ClinicianClients } from "@/components/clinician/ClinicianClients";
import { ClinicianSettings } from "@/components/clinician/ClinicianSettings";
import { ClinicianNotifications } from "@/components/clinician/ClinicianNotifications";
import { ClinicianOnboarding } from "@/components/clinician/ClinicianOnboarding";
import { InvoiceExport } from "@/components/clinician/InvoiceExport";
import { ClinicianMessages } from "@/components/clinician/ClinicianMessages";
import { AppointmentRequests } from "@/components/clinician/AppointmentRequests";
import { ClinicalToolsHub } from "@/components/clinician/ClinicalToolsHub";

export default function ClinicianPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const {
    profile,
    clients,
    sessions,
    subscription,
    clientCount,
    maxClients,
    isClinicianMode,
    loadAllData,
    createProfile,
    updateProfile,
    createSession,
    updateSession,
    deleteSession,
    sendInvitation,
    canAddClient,
    loading: dataLoading
  } = useClinicianPortal();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/auth");
          return;
        }
        setUser(user);
      } catch (error) {
        console.error('[ClinicianPortal] Access check failed:', error);
        navigate("/auth");
      } finally {
        setLoading(false);
      }
    };
    checkAccess();
  }, [navigate]);

  if (loading || dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show onboarding if not set up as clinician
  if (!isClinicianMode) {
    return (
      <ClinicianOnboarding 
        onComplete={async (profileData) => {
          await createProfile(profileData);
          await loadAllData();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/30 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {profile?.practice_name || 'Therapist Portal'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {subscription?.tier?.name || 'Free'} · {clientCount}/{maxClients || '∞'} clients
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ClinicianNotifications clinicianId={user?.id} />
            {!canAddClient && (
              <Button 
                variant="default" 
                size="sm"
                onClick={() => navigate('/clinician/upgrade')}
                className="gap-2"
              >
                <CreditCard className="h-4 w-4" />
                Upgrade
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7 gap-2 bg-card/50 p-1">
            <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Calendar</span>
            </TabsTrigger>
            <TabsTrigger value="clients" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Clients</span>
            </TabsTrigger>
            <TabsTrigger value="tools" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Stethoscope className="h-4 w-4" />
              <span className="hidden sm:inline">Clinical</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Messages</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Invoices</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <ClinicianDashboard 
              clients={clients}
              sessions={sessions}
              subscription={subscription}
              clientCount={clientCount}
              maxClients={maxClients}
              clinicianId={user?.id || ''}
            />
          </TabsContent>

          <TabsContent value="calendar">
            <ClinicianCalendar 
              sessions={sessions}
              clients={clients}
              profile={profile}
              onCreateSession={createSession}
              onUpdateSession={updateSession}
              onDeleteSession={deleteSession}
            />
          </TabsContent>

          <TabsContent value="clients">
            <ClinicianClients 
              clients={clients}
              sessions={sessions}
              canAddClient={canAddClient}
              clinicianId={user?.id || ''}
              onInviteClient={sendInvitation}
              onRefresh={loadAllData}
            />
          </TabsContent>

          <TabsContent value="tools">
            <ClinicalToolsHub 
              clinicianId={user?.id || ''} 
              clients={clients} 
              sessions={sessions} 
            />
          </TabsContent>

          <TabsContent value="messages">
            <div className="space-y-6">
              <AppointmentRequests 
                clinicianId={user?.id || ''} 
                clients={clients}
                onSessionCreated={loadAllData}
              />
              <ClinicianMessages 
                clinicianId={user?.id || ''} 
                clients={clients}
              />
            </div>
          </TabsContent>

          <TabsContent value="notes">
            <div className="max-w-4xl">
              <InvoiceExport />
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <ClinicianSettings 
              profile={profile}
              subscription={subscription}
              onUpdateProfile={updateProfile}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
